// User WebSocket Hook for Real-time User-level Notifications
import { useState, useEffect, useCallback, useRef } from 'react';
import userWebSocketService from '../../../services/userWebSocketService';
import { useAuth } from '../../../contexts/AuthContext';
import { showInfo } from '../../../utils/toast';
import { TOAST_DURATIONS } from '../../../constants/toastConstants';

export const useUserWebSocket = () => {
  const { user, logout } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [userStatus, setUserStatus] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  
  // Refs to store the latest values
  const userRef = useRef(user);
  const connectionStatusRef = useRef(connectionStatus);
  const connectingRef = useRef(false);
  const connectTimeoutRef = useRef(null);
  const roleChangeLogoutInProgressRef = useRef(false);

  // Update refs when values change
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  useEffect(() => {
    connectingRef.current = isConnecting;
  }, [isConnecting]);

  // Connect to User WebSocket
  const connect = useCallback(async (userId = null) => {
    const targetUserId = userId || userRef.current?.id;
    
    // console.log('👤 User WebSocket connect called:', {
    //   userId,
    //   targetUserId,
    //   currentStatus: connectionStatusRef.current,
    //   isConnecting
    // });
    
    if (!targetUserId) {
      console.error('❌ No user ID available for User WebSocket connection');
      return;
    }

    if (connectionStatusRef.current === 'connected' || connectionStatusRef.current === 'connecting' || connectingRef.current) {
      //console.log('👤 User WebSocket already connected or connecting, skipping');
      return;
    }

    //console.log('👤 Attempting to connect User WebSocket...');
    connectingRef.current = true;
    setIsConnecting(true);
    
    try {
      await userWebSocketService.connect(targetUserId);
      //console.log('👤 User WebSocket service connect called successfully');
    } catch (error) {
      connectingRef.current = false;
      setIsConnecting(false);
    }
  }, []);

  // Disconnect from User WebSocket
  const disconnect = useCallback(() => {
    userWebSocketService.disconnect();
    setConnectionStatus('disconnected');
    setIsConnecting(false);
    setIsReconnecting(false);
  }, []);

  // Send user status update
  const sendUserStatusUpdate = useCallback((status, metadata = {}) => {
    if (userWebSocketService.isConnected()) {
      userWebSocketService.sendUserStatusUpdate(status, metadata);
    } else {
      console.warn('⚠️ User WebSocket not connected, cannot send status update');
    }
  }, []);

  // Send user activity update
  const sendUserActivityUpdate = useCallback((activity, details = {}) => {
    if (userWebSocketService.isConnected()) {
      userWebSocketService.sendUserActivityUpdate(activity, details);
    } else {
      console.warn('⚠️ User WebSocket not connected, cannot send activity update');
    }
  }, []);

  // Send custom message
  const sendMessage = useCallback((messageData) => {
    if (userWebSocketService.isConnected()) {
      userWebSocketService.sendMessage(messageData);
    } else {
      console.warn('⚠️ User WebSocket not connected, cannot send message');
    }
  }, []);

  // Manual reconnect
  const manualReconnect = useCallback(async () => {
    setIsReconnecting(true);
    try {
      await userWebSocketService.manualReconnect();
    } catch (error) {
      console.error('❌ Error during manual reconnect:', error);
      setIsReconnecting(false);
    }
  }, []);

  // Handle user notifications
  const handleUserNotification = useCallback((data) => {
    //console.log('📢 User notification received:', data);
    
    const notification = {
      id: `user_notification_${Date.now()}`,
      type: 'user_notification',
      title: data.title || 'User Notification',
      message: data.message || 'You have a new notification',
      data: data,
      timestamp: new Date().toISOString(),
      priority: data.priority || 'medium'
    };
    
    setNotifications(prev => [notification, ...prev.slice(0, 49)]); // Keep last 50 notifications
    
    // Dispatch custom event for useChat hook
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'user_notification', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle user alerts
  const handleUserAlert = useCallback((data) => {
    //console.log('🚨 User alert received:', data);
    
    const alert = {
      id: `user_alert_${Date.now()}`,
      type: 'user_alert',
      title: data.title || 'User Alert',
      message: data.message || 'You have a new alert',
      data: data,
      timestamp: new Date().toISOString(),
      priority: data.priority || 'high'
    };
    
    setNotifications(prev => [alert, ...prev.slice(0, 49)]); // Keep last 50 notifications
    
    // Dispatch custom event for useChat hook
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'user_alert', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle user status updates
  const handleUserStatusUpdate = useCallback((data) => {
    //console.log('👤 User status update received:', data);
    setUserStatus(data);
    
    // Dispatch custom event for useChat hook
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'user_status_update', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle user permission updates
  const handleUserPermissionUpdate = useCallback((data) => {
    //console.log('🔐 User permission update received:', data);
    
    const notification = {
      id: `user_permission_${Date.now()}`,
      type: 'user_permission_update',
      title: 'Permission Update',
      message: data.message || 'Your permissions have been updated',
      data: data,
      timestamp: new Date().toISOString(),
      priority: 'medium'
    };
    
    setNotifications(prev => [notification, ...prev.slice(0, 49)]);
    
    // Dispatch custom event for useChat hook
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'user_permission_update', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle user profile updates
  const handleUserProfileUpdate = useCallback((data) => {
    //console.log('👤 User profile update received:', data);
    
    const notification = {
      id: `user_profile_${Date.now()}`,
      type: 'user_profile_update',
      title: 'Profile Update',
      message: data.message || 'Your profile has been updated',
      data: data,
      timestamp: new Date().toISOString(),
      priority: 'low'
    };
    
    setNotifications(prev => [notification, ...prev.slice(0, 49)]);
    
    // Dispatch custom event for useChat hook
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'user_profile_update', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle user assignment updates
  const handleUserAssignmentUpdate = useCallback((data) => {
    //console.log('📋 User assignment update received:', data);
    
    const notification = {
      id: `user_assignment_${Date.now()}`,
      type: 'user_assignment_update',
      title: 'Assignment Update',
      message: data.message || 'Your assignments have been updated',
      data: data,
      timestamp: new Date().toISOString(),
      priority: 'medium'
    };
    
    setNotifications(prev => [notification, ...prev.slice(0, 49)]);
    
    // Dispatch custom event for useChat hook
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'user_assignment_update', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle user online status
  const handleUserOnlineStatus = useCallback((data) => {
    //console.log('🟢 User online status received:', data);
    setUserStatus(prev => ({ ...prev, online: data.online, lastSeen: data.lastSeen }));
    
    // Dispatch custom event for useChat hook
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'user_online_status', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle user activity updates
  const handleUserActivityUpdate = useCallback((data) => {
    //console.log('📊 User activity update received:', data);
    setUserActivity(data);
    
    // Dispatch custom event for useChat hook
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'user_activity_update', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle conversation assignment
  const handleConversationAssigned = useCallback((data) => {
    //console.log('📋 Conversation assigned received in useUserWebSocket:', data);
    
    const notification = {
      id: `conversation_assigned_${Date.now()}`,
      type: 'conversation_assigned',
      title: 'Conversation Assigned',
      message: `You have been assigned to conversation ${data.conversation?.id || 'Unknown'}`,
      data: data,
      timestamp: new Date().toISOString(),
      priority: 'high'
    };
    
    setNotifications(prev => [notification, ...prev.slice(0, 49)]);
    
    // Dispatch custom event for useChat hook
    //console.log('📋 Dispatching user_websocket_message event for conversation.assigned');
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'conversation.assigned', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle assignment pending (transfer)
  const handleAssignmentPending = useCallback((data) => {
    //console.log('⏳ Assignment pending received in useUserWebSocket:', data);
    
    const notification = {
      id: `assignment_pending_${Date.now()}`,
      type: 'assignment_pending',
      title: 'Chat Transfer Pending',
      message: `A chat has been transferred to you`,
      data: data,
      timestamp: new Date().toISOString(),
      priority: 'high'
    };
    
    setNotifications(prev => [notification, ...prev.slice(0, 49)]);
    
    // Dispatch custom event for useChat hook
    //console.log('⏳ Dispatching user_websocket_message event for assignment.pending');
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'assignment.pending', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  const handleInvitePending = useCallback((data) => {
    const notification = {
      id: `invite_pending_${Date.now()}`,
      type: 'invite_pending',
      title: 'Chat Invitation',
      message: `You have been invited to a conversation`,
      data: data,
      timestamp: new Date().toISOString(),
      priority: 'high'
    };

    setNotifications(prev => [notification, ...prev.slice(0, 49)]);

    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'invite.pending', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  const handleInviteAccept = useCallback((data) => {
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'invite.accept', data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  const handleInviteReject = useCallback((data) => {
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'invite.reject', data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  const handleInviteNew = useCallback((data) => {
    window.dispatchEvent(
      new CustomEvent('user_websocket_message', {
        detail: { type: 'invite.new', data },
      })
    );
  }, []);

  // Forward user-scope message events into the common message pipeline
  // so indicators update when another conversation is open.
  const handleUserMessage = useCallback((data) => {
    const customEvent = new CustomEvent('websocket_message', {
      detail: { type: 'message', data }
    });
    window.dispatchEvent(customEvent);
  }, []);


  // Handle conversation updated event
  const handleConversationUpdated = useCallback((data) => {
    //console.log('🔄 Conversation updated via User WebSocket:', data);
    
    // Dispatch custom event for useChat hook to handle notifications
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'conversation.updated', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle conversation accepted event
  // NOTE: This handler is kept for backward compatibility but conversation.accepted
  // should not be emitted from user WebSocket service (it's handled by tenant/conversation levels)
  const handleConversationAccepted = useCallback((data) => {
    // Skip - conversation.accepted should only come from tenant-level or conversation-level WebSocket
    // User-level WebSocket is for user-specific events like transfers, assignments, inbox items
    //console.log('⏭️ Skipping conversation.accepted via User WebSocket (should use tenant/conversation level)');
    return;
  }, []);

  // Handle transfer accept event
  const handleTransferAccept = useCallback((data) => {
    console.log('👤 USER WEBSOCKET HOOK: Transfer accepted via User WebSocket:', data);
    console.log('👤 USER WEBSOCKET HOOK: Conversation ID:', data?.id);
    console.log('👤 USER WEBSOCKET HOOK: Owner ID:', data?.owner);
    console.log('👤 USER WEBSOCKET HOOK: Participants:', data?.participants);
    console.log('👤 USER WEBSOCKET HOOK: Assignment:', data?.assignment);
    
    // Dispatch custom event for useChat hook to handle notifications
    console.log('📡 USER WEBSOCKET HOOK: Dispatching user_websocket_message event');
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'transfer.accept', data: data }
    });
    window.dispatchEvent(customEvent);
    console.log('✅ USER WEBSOCKET HOOK: user_websocket_message event dispatched successfully');
  }, []);

  // Handle transfer reject event
  const handleTransferReject = useCallback((data) => {
    // Add unique processing ID to prevent duplicate processing
    const processingId = `transfer_reject_${data?.conversation?.id}_${Date.now()}`;
    const lastProcessedKey = `last_processed_${data?.conversation?.id}`;
    const lastProcessed = localStorage.getItem(lastProcessedKey);
    const now = Date.now();
    
    // If the same event was processed recently (within 1 second), skip it
    if (lastProcessed && (now - parseInt(lastProcessed)) < 1000) {
      console.log('🚫 USER WEBSOCKET HOOK: Skipping duplicate transfer.reject event for conversation:', data?.conversation?.id);
      return;
    }
    
    // Store processing time to prevent duplicates
    localStorage.setItem(lastProcessedKey, now.toString());
    
    console.log('👤 USER WEBSOCKET HOOK: Transfer rejected via User WebSocket:', data);
    console.log('👤 USER WEBSOCKET HOOK: Conversation ID:', data?.conversation?.id);
    console.log('👤 USER WEBSOCKET HOOK: Owner ID:', data?.conversation?.owner);
    console.log('👤 USER WEBSOCKET HOOK: Participants:', data?.conversation?.participants);
    console.log('👤 USER WEBSOCKET HOOK: Assignment:', data?.conversation?.assignment);
    
    // Dispatch custom event for useChat hook to handle notifications
    console.log('📡 USER WEBSOCKET HOOK: Dispatching user_websocket_message event');
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'transfer.reject', data: data }
    });
    window.dispatchEvent(customEvent);
    console.log('✅ USER WEBSOCKET HOOK: user_websocket_message event dispatched successfully');
  }, []);

  // Handle message status updates
  const handleMessageStatusUpdated = useCallback((data) => {
    console.log('📊 Message status updated received in useUserWebSocket:', data);

    // Dispatch custom event for useChat hook
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'message_status_updated', data: data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle role changed event for current logged-in user
  const handleUserRoleChanged = useCallback((data) => {
    const targetUserId = Number(data?.user_id);
    const currentUserId = Number(userRef.current?.id);
    if (!targetUserId || !currentUserId || targetUserId !== currentUserId) return;

    const roleChangedNotification = {
      id: `user_role_changed_${Date.now()}`,
      type: 'user_role_changed',
      title: 'Role Updated',
      message: data?.message || 'Your role has been updated.',
      data,
      timestamp: new Date().toISOString(),
      priority: 'high'
    };

    setNotifications(prev => [roleChangedNotification, ...prev.slice(0, 49)]);
    showInfo(roleChangedNotification.message, '', { duration: TOAST_DURATIONS.STANDARD });

    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'user.role.changed', data }
    });
    window.dispatchEvent(customEvent);

    // Force logout after toast disappears (same flow as manual logout API call)
    if (roleChangeLogoutInProgressRef.current) return;
    roleChangeLogoutInProgressRef.current = true;
    setTimeout(async () => {
      try {
        await logout();
      } finally {
        if (window.location.pathname !== '/login') {
          window.location.replace('/login');
        } else {
          window.location.reload();
        }
      }
    }, TOAST_DURATIONS.STANDARD + 200);
  }, [logout]);

  // Handle chat transferred event
  const handleChatTransferred = useCallback((data) => {
    console.log('👤 USER WEBSOCKET HOOK: Chat transferred via User WebSocket:', data);
    console.log('👤 USER WEBSOCKET HOOK: Chat ID:', data?.chat_id);
    console.log('👤 USER WEBSOCKET HOOK: Transferred by:', data?.transferred_by);
    console.log('👤 USER WEBSOCKET HOOK: Transferred to:', data?.transferred_to);
    console.log('👤 USER WEBSOCKET HOOK: Transferred by name:', data?.transferred_by_name);
    console.log('👤 USER WEBSOCKET HOOK: Transferred to name:', data?.transferred_to_name);
    
    // Dispatch custom event for useChat hook to handle notifications
    console.log('📡 USER WEBSOCKET HOOK: Dispatching user_websocket_message event for chat_transferred');
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'chat_transferred', data: data }
    });
    window.dispatchEvent(customEvent);
    console.log('✅ USER WEBSOCKET HOOK: user_websocket_message event dispatched successfully');
  }, []);

  // Handle conversation removed event (e.g. unassigned from My tab)
  const handleConversationRemoved = useCallback((data) => {
    const customEvent = new CustomEvent('user_websocket_message', {
      detail: { type: 'conversation.removed', data }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // Handle connection events
  const handleConnected = useCallback(() => {
    //console.log('✅ User WebSocket connected');
    setConnectionStatus('connected');
    connectingRef.current = false;
    setIsConnecting(false);
    setIsReconnecting(false);
  }, []);

  const handleDisconnected = useCallback(() => {
    //console.log('❌ User WebSocket disconnected');
    setConnectionStatus('disconnected');
    connectingRef.current = false;
    setIsConnecting(false);
    setIsReconnecting(false);
  }, []);

  const handleConnecting = useCallback((data) => {
    //console.log('🔄 User WebSocket connecting:', data);
    setConnectionStatus('connecting');
    if (data.reason === 'reconnecting') {
      setIsReconnecting(true);
    }
  }, []);

  const handleError = useCallback((error) => {
    console.error('❌ User WebSocket error:', error);
    setConnectionStatus('error');
    connectingRef.current = false;
    setIsConnecting(false);
  }, []);

  const handleMaxReconnectAttempts = useCallback(() => {
    console.error('❌ Max reconnection attempts reached for User WebSocket');
    setConnectionStatus('failed');
    setIsReconnecting(false);
  }, []);

  // Setup event listeners
  useEffect(() => {
    // Connection events
    userWebSocketService.on('connected', handleConnected);
    userWebSocketService.on('disconnected', handleDisconnected);
    userWebSocketService.on('connecting', handleConnecting);
    userWebSocketService.on('error', handleError);
    userWebSocketService.on('max_reconnect_attempts_reached', handleMaxReconnectAttempts);

    // User-specific events
    userWebSocketService.on('user_notification', handleUserNotification);
    userWebSocketService.on('user_alert', handleUserAlert);
    userWebSocketService.on('user_status_update', handleUserStatusUpdate);
    userWebSocketService.on('user_permission_update', handleUserPermissionUpdate);
    userWebSocketService.on('user_profile_update', handleUserProfileUpdate);
    userWebSocketService.on('user_assignment_update', handleUserAssignmentUpdate);
    userWebSocketService.on('user_online_status', handleUserOnlineStatus);
    userWebSocketService.on('user_activity_update', handleUserActivityUpdate);
    userWebSocketService.on('conversation.assigned', handleConversationAssigned);
    userWebSocketService.on('assignment.pending', handleAssignmentPending);
    userWebSocketService.on('invite.pending', handleInvitePending);
    userWebSocketService.on('invite.new', handleInviteNew);
    userWebSocketService.on('invite.accept', handleInviteAccept);
    userWebSocketService.on('invite.reject', handleInviteReject);
    userWebSocketService.on('message', handleUserMessage);
    userWebSocketService.on('conversation.updated', handleConversationUpdated);
    userWebSocketService.on('conversation.accepted', handleConversationAccepted);
    userWebSocketService.on('transfer.accept', handleTransferAccept);
    userWebSocketService.on('transfer.reject', handleTransferReject);
    userWebSocketService.on('chat_transferred', handleChatTransferred);
    userWebSocketService.on('conversation.removed', handleConversationRemoved);
    userWebSocketService.on('message_status_updated', handleMessageStatusUpdated);
    userWebSocketService.on('user.role.changed', handleUserRoleChanged);
    userWebSocketService.on('role_update', handleUserRoleChanged);

    // Cleanup
    return () => {
      userWebSocketService.off('connected', handleConnected);
      userWebSocketService.off('disconnected', handleDisconnected);
      userWebSocketService.off('connecting', handleConnecting);
      userWebSocketService.off('error', handleError);
      userWebSocketService.off('max_reconnect_attempts_reached', handleMaxReconnectAttempts);
      
      userWebSocketService.off('user_notification', handleUserNotification);
      userWebSocketService.off('user_alert', handleUserAlert);
      userWebSocketService.off('user_status_update', handleUserStatusUpdate);
      userWebSocketService.off('user_permission_update', handleUserPermissionUpdate);
      userWebSocketService.off('user_profile_update', handleUserProfileUpdate);
      userWebSocketService.off('user_assignment_update', handleUserAssignmentUpdate);
      userWebSocketService.off('user_online_status', handleUserOnlineStatus);
      userWebSocketService.off('user_activity_update', handleUserActivityUpdate);
      userWebSocketService.off('conversation.assigned', handleConversationAssigned);
      userWebSocketService.off('assignment.pending', handleAssignmentPending);
      userWebSocketService.off('invite.pending', handleInvitePending);
      userWebSocketService.off('invite.new', handleInviteNew);
      userWebSocketService.off('invite.accept', handleInviteAccept);
      userWebSocketService.off('invite.reject', handleInviteReject);
      userWebSocketService.off('message', handleUserMessage);
      userWebSocketService.off('conversation.updated', handleConversationUpdated);
      userWebSocketService.off('conversation.accepted', handleConversationAccepted);
      userWebSocketService.off('transfer.accept', handleTransferAccept);
      userWebSocketService.off('transfer.reject', handleTransferReject);
      userWebSocketService.off('chat_transferred', handleChatTransferred);
      userWebSocketService.off('conversation.removed', handleConversationRemoved);
      userWebSocketService.off('message_status_updated', handleMessageStatusUpdated);
      userWebSocketService.off('user.role.changed', handleUserRoleChanged);
      userWebSocketService.off('role_update', handleUserRoleChanged);
    };
  }, []);

  // Auto-connect when user is available and handle user changes
  useEffect(() => {
    // Clear any existing timeout
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }

    if (user?.id) {
      // Update connection parameters
      userWebSocketService.updateConnectionParams(user.id);
      
      // Only connect if not already connected or connecting
      if (connectionStatus === 'disconnected' && !connectingRef.current) {
        //console.log('👤 Scheduling User WebSocket connection for user:', user.id);
        // Debounce connection attempts
        connectTimeoutRef.current = setTimeout(() => {
          if (connectionStatusRef.current === 'disconnected' && !connectingRef.current) {
            //console.log('👤 Auto-connecting User WebSocket for user:', user.id);
            connect(user.id);
          }
        }, 1000); // 1 second delay
      }
    } else {
      // User logged out, disconnect WebSocket
      //console.log('👤 User logged out, disconnecting User WebSocket');
      disconnect();
    }

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
    };
  }, [user?.id, connect, disconnect, connectionStatus]);

  // Cleanup on unmount - DON'T disconnect WebSocket (it should stay active globally)
  useEffect(() => {
    return () => {
      //console.log('👤 User WebSocket hook unmounting, cleaning up timeouts...');
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      // NOTE: We DON'T disconnect the WebSocket here because it should remain 
      // active globally across all screens, not just the chat page
      // It will be disconnected by the WebSocketProvider on logout
    };
  }, []);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Clear specific notification
  const clearNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
  }, []);

  return {
    // Connection state
    connectionStatus,
    isConnecting,
    isReconnecting,
    isConnected: connectionStatus === 'connected',
    
    // Data
    notifications,
    userStatus,
    userActivity,
    
    // Actions
    connect,
    disconnect,
    sendUserStatusUpdate,
    sendUserActivityUpdate,
    sendMessage,
    manualReconnect,
    clearNotifications,
    clearNotification
  };
};

export default useUserWebSocket;
